/**
 * MFA Enrolment - Wave 6 (2026-05-27).
 *
 * Mirrors the Core pattern at
 * G:/VaultConsent/apps/web/src/pages/platform/platform-mfa-
 * enrollment-page.tsx but rewired to Discovery's
 * /v1/auth/mfa/{enroll,verify} endpoints (not Cognito).
 *
 * Three-step flow:
 *   1. Initiate: POST /v1/auth/mfa/enroll → returns QR data-URL +
 *      base32 secret + 10 backup codes (shown ONCE).
 *   2. User scans QR in their authenticator app + saves backup codes.
 *   3. Submit first TOTP → POST /v1/auth/mfa/verify → mints full
 *      session JWT, navigates home.
 *
 * Reads the partial-auth token from the URL fragment (`#t=<token>`)
 * - Login.tsx writes it there when login returns mfaChallengeRequired
 * with reason=mfa_enrolment_due - keeps it in memory, removes it from the
 * address bar, and sends it only as `Authorization: Bearer` (mfa-handoff.ts,
 * lib/api.ts). A refusal of the token or of the account ends this step: back
 * to sign-in with the reason (the token lives five minutes, so a person who
 * takes longer than that between the QR code and the first code signs in
 * again and starts the enrolment afresh). A wrong code keeps them here.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ShieldCheck,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Copy,
  Smartphone,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Mark } from '@/components/brand/Mark';
import {
  mfaEnrol,
  mfaSignInAgainReason,
  mfaVerifyEnrol,
  type MfaEnrolResult,
  setAuth,
} from '@/lib/api';
import { MFA_ERROR_PARAM, readPartialAuthFromHash, scrubPartialAuthFromUrl } from './mfa-handoff';

type Step = 'init' | 'show_qr' | 'verify' | 'done';

export function MfaEnrolPage(): JSX.Element {
  const navigate = useNavigate();
  // Read during the first render (pure), scrubbed from the URL after it.
  const [partialAuth] = useState<string | null>(readPartialAuthFromHash);
  const [step, setStep] = useState<Step>('init');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [setupResult, setSetupResult] = useState<MfaEnrolResult | null>(null);
  const [savedCodes, setSavedCodes] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);

  useEffect(() => {
    scrubPartialAuthFromUrl();
  }, []);

  function backToSignIn(): void {
    void navigate({ to: '/login', replace: true });
  }

  /** True when the refusal ends the step (and the page has navigated away). */
  function endedStep(e: unknown): boolean {
    const again = mfaSignInAgainReason(e);
    if (!again) return false;
    void navigate({ to: '/login', search: { [MFA_ERROR_PARAM]: again } as Record<string, unknown>, replace: true });
    return true;
  }

  async function startSetup(): Promise<void> {
    if (!partialAuth) return;
    setError('');
    setLoading(true);
    try {
      const result = await mfaEnrol(partialAuth);
      setSetupResult(result);
      setStep('show_qr');
    } catch (e) {
      if (endedStep(e)) return;
      setError(e instanceof Error ? e.message : 'enrolment failed');
    } finally {
      setLoading(false);
    }
  }

  async function submitVerify(): Promise<void> {
    if (!partialAuth || verifyCode.length !== 6) return;
    setError('');
    setLoading(true);
    try {
      const result = await mfaVerifyEnrol(partialAuth, verifyCode);
      // Persist the real session JWT minted by /verify (never the partial token).
      setAuth(result.session.token, result.user, result.session.expiresAt);
      setStep('done');
      setTimeout(() => void navigate({ to: '/', replace: true }), 800);
    } catch (e) {
      if (endedStep(e)) return;
      setError(e instanceof Error ? e.message : 'verification failed');
    } finally {
      setLoading(false);
    }
  }

  function copySecret(): void {
    if (!setupResult) return;
    void navigator.clipboard.writeText(setupResult.secretBase32);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 1500);
  }

  function copyBackupCodes(): void {
    if (!setupResult) return;
    void navigator.clipboard.writeText(setupResult.backupCodes.join('\n'));
    setCopiedCodes(true);
    setTimeout(() => setCopiedCodes(false), 1500);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6 aurora">
      <div className="absolute inset-0 mesh-grid opacity-30 pointer-events-none" />
      <div className="w-full max-w-xl relative z-10">
        <div className="flex justify-center mb-8">
          <Mark variant="wordmark" size={44} animated />
        </div>

        <GlassCard className="p-6">
          <CardHeader className="p-0 mb-4 text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" /> Enable multi-factor auth
            </CardTitle>
            <CardDescription>
              Your role requires MFA. Use Google Authenticator, Microsoft Authenticator, or Authy.
            </CardDescription>
          </CardHeader>

          {!partialAuth && (
            <div className="mb-4 glass-lite border border-destructive/40 rounded-md p-3 text-xs text-destructive space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  This setup step has no sign-in to finish (the page was reloaded or opened directly).
                  Sign in again with your email and password to begin MFA enrolment.
                </span>
              </div>
              <Button size="sm" variant="glass" className="w-full" onClick={backToSignIn}>
                Back to sign-in
              </Button>
            </div>
          )}

          {error && (
            <div className="mb-4 glass-lite border border-destructive/40 rounded-md p-3 text-xs text-destructive flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {step === 'init' && (
            <div className="space-y-4">
              <div className="text-xs text-muted-foreground space-y-2">
                <p>What we'll do next:</p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Generate a TOTP secret + scan-ready QR code.</li>
                  <li>Issue 10 single-use backup codes - save them somewhere safe.</li>
                  <li>You enter the first 6-digit code from your authenticator to confirm.</li>
                </ol>
              </div>
              <Button
                className="w-full"
                onClick={() => void startSetup()}
                disabled={loading || !partialAuth}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Smartphone className="h-4 w-4 mr-2" />}
                {loading ? 'Generating…' : 'Start MFA setup'}
              </Button>
            </div>
          )}

          {step === 'show_qr' && setupResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                    1. Scan with your authenticator
                  </div>
                  <img
                    src={setupResult.qrCodeDataUrl}
                    alt="MFA QR code"
                    className="mx-auto rounded-md bg-white p-2 w-[240px] h-[240px]"
                  />
                  <div className="mt-3">
                    <div className="text-[10px] uppercase text-muted-foreground">Can't scan? Enter manually:</div>
                    <div className="mt-1 flex items-center justify-center gap-2">
                      <code className="font-mono text-[11px] break-all glass-lite px-2 py-1 rounded">
                        {setupResult.secretBase32}
                      </code>
                      <Button size="sm" variant="glass" onClick={copySecret}>
                        {copiedSecret ? <CheckCircle2 className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      </Button>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                    2. Save your backup codes
                  </div>
                  <div className="glass-lite border border-warning/40 rounded-md p-3">
                    <p className="text-[11px] text-warning mb-2">
                      <AlertTriangle className="h-3 w-3 inline mr-1" />
                      Shown ONCE. Print or copy now - they let you recover access if you lose your authenticator.
                    </p>
                    <div className="grid grid-cols-2 gap-1 font-mono text-[11px] text-center">
                      {setupResult.backupCodes.map((c) => (
                        <code key={c} className="bg-background/40 px-1.5 py-0.5 rounded">
                          {c}
                        </code>
                      ))}
                    </div>
                    <Button size="sm" variant="glass" className="w-full mt-2" onClick={copyBackupCodes}>
                      {copiedCodes ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                      {copiedCodes ? 'Copied all 10' : 'Copy all'}
                    </Button>
                  </div>
                  <label className="flex items-start gap-2 mt-3 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={savedCodes}
                      onChange={(e) => setSavedCodes(e.target.checked)}
                      className="h-4 w-4 mt-0.5 accent-primary"
                    />
                    <span>I've saved my backup codes somewhere safe.</span>
                  </label>
                </div>
              </div>
              <Button
                className="w-full"
                onClick={() => setStep('verify')}
                disabled={!savedCodes}
              >
                Continue to verification
              </Button>
            </div>
          )}

          {step === 'verify' && (
            <div className="space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                  3. Enter the 6-digit code from your authenticator
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  autoFocus
                  className="glass-lite w-full px-4 py-3 rounded-md text-center text-xl font-mono tracking-widest border border-border/40 focus:outline-none focus:border-primary"
                />
              </div>
              <Button
                className="w-full"
                onClick={() => void submitVerify()}
                disabled={loading || verifyCode.length !== 6}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                {loading ? 'Verifying…' : 'Verify & enable MFA'}
              </Button>
              <button
                type="button"
                className="text-[11px] text-muted-foreground underline w-full text-center"
                onClick={() => setStep('show_qr')}
              >
                Back to QR code
              </button>
            </div>
          )}

          {step === 'done' && (
            <div className="text-center space-y-3 py-6">
              <CheckCircle2 className="h-12 w-12 mx-auto text-success" />
              <h3 className="text-sm font-semibold">MFA enabled</h3>
              <p className="text-xs text-muted-foreground">
                Redirecting you to the dashboard…
              </p>
              <Badge variant="success">Account secured</Badge>
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
